import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';
import { CheckCircle, XCircle, SpinnerGap } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [resendEmail, setResendEmail] = useState(searchParams.get('email') || '');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    verifyEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyEmail = async () => {
    try {
      const emailParam = resendEmail ? `&email=${encodeURIComponent(resendEmail)}` : '';
      const res = await axios.get(`${API}/auth/verify-email?token=${token}${emailParam}`);
      setStatus(res.data.already_verified ? 'already' : 'success');
    } catch {
      setStatus('error');
    }
  };

  const handleResend = async () => {
    if (!resendEmail) return;
    setResending(true);
    setResendMsg(null);
    try {
      const res = await axios.post(`${API}/auth/resend-verification`, { email: resendEmail });
      if (res.data.already_verified) {
        setResendMsg({ type: 'ok', text: 'Questa email è già verificata. Puoi accedere direttamente.' });
      } else {
        setResendMsg({ type: 'ok', text: 'Nuovo link inviato! Controlla la casella email (anche spam).' });
      }
    } catch (e) {
      setResendMsg({ type: 'err', text: e?.response?.data?.detail || 'Errore invio, riprova tra qualche minuto.' });
    }
    setResending(false);
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="verify-email-page">
      <div className="bg-white max-w-md w-full p-8 shadow-sm border border-zinc-200 text-center">
        <div className="mb-6">
          <Logo size="xl" />
        </div>

        {status === 'loading' && (
          <div>
            <SpinnerGap size={40} className="text-zinc-400 mx-auto animate-spin mb-4" />
            <p className="text-zinc-500">Attivazione in corso...</p>
          </div>
        )}

        {status === 'success' && (
          <div>
            <CheckCircle size={48} weight="fill" className="text-green-600 mx-auto mb-4" />
            <h2 className="text-xl font-black text-zinc-900 mb-2">Account attivato!</h2>
            <p className="text-sm text-zinc-500 mb-6">
              Il tuo account è stato verificato. Ora puoi accedere e iniziare la prova gratuita di 7 giorni.
            </p>
            <Link
              to="/login"
              className="inline-block w-full py-3 bg-zinc-900 text-white text-sm font-bold uppercase tracking-wider hover:bg-zinc-800 transition-colors"
              data-testid="verify-login-btn"
            >
              Accedi
            </Link>
          </div>
        )}

        {status === 'already' && (
          <div>
            <CheckCircle size={48} weight="fill" className="text-blue-500 mx-auto mb-4" />
            <h2 className="text-xl font-black text-zinc-900 mb-2">Gia verificato</h2>
            <p className="text-sm text-zinc-500 mb-6">Il tuo account è gia attivo.</p>
            <Link
              to="/login"
              className="inline-block w-full py-3 bg-zinc-900 text-white text-sm font-bold uppercase tracking-wider hover:bg-zinc-800 transition-colors"
              data-testid="verify-login-btn"
            >
              Accedi
            </Link>
          </div>
        )}

        {(status === 'error' || status === 'invalid') && (
          <div>
            <XCircle size={48} weight="fill" className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-black text-zinc-900 mb-2">Link non valido</h2>
            <p className="text-sm text-zinc-500 mb-5 leading-relaxed">
              Questo link di attivazione non funziona. Di solito succede perché è già stato usato
              o sono passati troppi giorni. <strong>Inserisci la tua email qui sotto</strong> e ti mandiamo un nuovo link.
            </p>
            <div className="space-y-3">
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="la-tua-email@esempio.it"
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm rounded-none focus:border-zinc-900 focus:outline-none"
                data-testid="verify-resend-email-input"
              />
              <button
                onClick={handleResend}
                disabled={resending || !resendEmail}
                className="inline-block w-full py-3 bg-zinc-900 text-white text-sm font-bold uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="verify-resend-btn"
              >
                {resending ? 'Invio...' : resendMsg ? '✓ Inviato' : 'Reinvia link'}
              </button>
              {resendMsg && (
                <p className={`text-xs mt-2 ${resendMsg.type === 'ok' ? 'text-emerald-700' : 'text-red-700'}`}>
                  {resendMsg.text}
                </p>
              )}
            </div>
            <p className="text-xs text-zinc-400 mt-5">
              Se sei già iscritta, puoi anche provare ad <Link to="/login" className="underline hover:text-zinc-700">accedere</Link> direttamente.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
